import {
  OrderBookConfigurationKeys,
  OrderBookConfigurations,
  ORDER_BOOK_NAMESPACE,
  SizeGridRowConfig,
  SIZE_GRID_ROWS_CONFIG_DEFAULT,
} from '@epicuro-next/data/order-book';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { selectPreferredScaleSet } from '@epicuro-next/state/user-state';
import { createSelector } from '@ngrx/store';
import { OrderType } from 'graphql-api/gql-api-interfaces';

import { mapItemsWithProducts } from '../../shared/map-items-with-products/map-items-with-products';
import { mergeOrderItemsWithUrls } from '../../shared/merge-order-items-with-urls/merge-order-items-with-urls';
import { selectOrderBookState } from '../../store';
import {
  adapter,
  orderDetailsFeatureKey,
} from '../reducers/order-details.reducer';

export const selectOrderDetailsState = createSelector(
  selectOrderBookState,
  (state) => state[orderDetailsFeatureKey],
);

export const {
  selectAll: selectOrderItems,
  selectIds: selectOrderItemsIds,
  selectTotal,
  selectEntities,
} = adapter.getSelectors(selectOrderDetailsState);

export const selectOrderDetails = createSelector(
  selectOrderDetailsState,
  (state) => state.details,
);

export const selectOrderSalesNumber = createSelector(
  selectOrderDetails,
  (state) => state?.salesOrderNumber ?? null,
);

export const selectOrderReferenceNumber = createSelector(
  selectOrderDetails,
  (state) => state?.clientReferenceNumber ?? null,
);

export const selectOrderItem = (uniqueOrderItemId: string) =>
  createSelector(selectEntities, (orderItems) => orderItems[uniqueOrderItemId]);

export const selectOrderProducts = createSelector(
  selectOrderDetailsState,
  (state) => state.products,
);

export const selectOrderDetailSizeGridRows = createSelector(
  selectConfigurationValueFrom<
    OrderBookConfigurationKeys.detailSizeGridRows,
    OrderBookConfigurations
  >(
    ORDER_BOOK_NAMESPACE,
    OrderBookConfigurationKeys.detailSizeGridRows,
    SIZE_GRID_ROWS_CONFIG_DEFAULT,
  ),
  (rows: SizeGridRowConfig[]) => {
    try {
      return JSON.parse(rows.toString());
    } catch (e) {
      return SIZE_GRID_ROWS_CONFIG_DEFAULT;
    }
  },
);

export const selectOrderItemsWithProductInfo = createSelector(
  selectOrderItems,
  selectOrderProducts,
  selectPreferredScaleSet,
  selectOrderDetailSizeGridRows,
  (items, products, scaleSet, sizeGridRows) =>
    mapItemsWithProducts(
      items,
      products,
      sizeGridRows as SizeGridRowConfig[],
      scaleSet,
    ),
);

export const selectOrderHeader = createSelector(
  selectOrderDetailsState,
  (state) => state.orderHeader,
);

export const selectOrderType = createSelector(
  selectOrderHeader,
  (orderHeader) =>
    orderHeader?.orderType &&
    OrderType[
      Object.keys(OrderType)[orderHeader?.orderType] as keyof typeof OrderType
    ],
);

export const selectUseDeliveryNoteUrl = selectConfigurationValueFrom<
  OrderBookConfigurationKeys.useDeliveryNoteUrl,
  OrderBookConfigurations
>(ORDER_BOOK_NAMESPACE, OrderBookConfigurationKeys.useDeliveryNoteUrl, false);

export const selectShowUnconfirmedQuantity = selectConfigurationValueFrom<
  OrderBookConfigurationKeys.showUnconfirmedQuantity,
  OrderBookConfigurations
>(
  ORDER_BOOK_NAMESPACE,
  OrderBookConfigurationKeys.showUnconfirmedQuantity,
  false,
);

export const selectOrderItemsWithMoreInfo = createSelector(
  selectOrderItemsWithProductInfo,
  selectOrderHeader,
  selectUseDeliveryNoteUrl,
  mergeOrderItemsWithUrls,
);

export const selectOrderDetailsLoading = createSelector(
  selectOrderDetailsState,
  (state) => state.loading,
);

export const selectOrderHeaderLoading = createSelector(
  selectOrderDetailsState,
  (state) => state.orderHeaderLoading,
);

export const selectOrderInvoices = createSelector(
  selectOrderHeader,
  (orderHeader) => orderHeader?.invoices || [],
);

export const selectOrderInvoicesCount = createSelector(
  selectOrderInvoices,
  (invoices) => invoices.length,
);

export const selectShouldShowStatusTooltips = selectConfigurationValueFrom<
  OrderBookConfigurationKeys.showStatusTooltips,
  OrderBookConfigurations
>(ORDER_BOOK_NAMESPACE, OrderBookConfigurationKeys.showStatusTooltips, false);

export const selectColumnsWithTooltip = selectConfigurationValueFrom<
  OrderBookConfigurationKeys.columnsWithTooltip,
  OrderBookConfigurations
>(ORDER_BOOK_NAMESPACE, OrderBookConfigurationKeys.columnsWithTooltip, []);

export const selectShowFallbackForEmptyValue = selectConfigurationValueFrom<
  OrderBookConfigurationKeys.showFallbackForEmptyValue,
  OrderBookConfigurations
>(
  ORDER_BOOK_NAMESPACE,
  OrderBookConfigurationKeys.showFallbackForEmptyValue,
  false,
);
